import { useAccoPriceDetails } from '@/scopes/products/pages/components/RecommendedAccos/hooks';
import { Icon, Modal, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components';
import { styled } from 'styled-components';
import { useIntl } from '@/intl';
import type { Price } from '@/scopes/products/domain/types';

interface AccoPriceDetailsProps {
  accommodationId: number;
  onClose: () => void;
  price: Price;
}

// Styling copied from react-pages/SearchPage/Results/ProductCard/PriceDetails.tsx
// TODO consider abstracting it away
const StyledErrorMessage = styled(SpaceBetween)`
  text-align: center;
  margin: 0 ${tk('spacing_md')};
  color: ${tk('color_semantic_text_strong')};

  @media ${tk('mq_tablet')} {
    margin: 0 ${tk('spacing_5xl')};
    max-height: calc(
      100vh - 120px
    ); // Set max height with viewport height minus modal header (56px) and spacing_4xl (64px) from Modal parent element, so content is not scrollable on smaller screens.
    height: 684px; // Set height with the modal's height (740px) from design minus modal header (56px).
  }
`;

// Styling copied from react-pages/SearchPage/Results/ProductCard/PriceDetails.tsx
// TODO consider abstracting it away
const StyledPriceDetails = styled(Typography)`
  color: ${tk('color_semantic_text')};
  padding: ${tk('spacing_md')};

  > * table {
    margin-bottom: ${tk('spacing_sm')};
  }

  > * p {
    margin-top: ${tk('spacing_sm')};
    margin-bottom: 0;
  }

  > * b + p {
    margin-top: ${tk('spacing_2xs')};
  }

  > * a {
    color: ${tk('color_semantic_text')};
  }

  // only apply margin-top to <b> elements that are not nested inside a <p> element
  > * b:not(p b) {
    display: inline-block;
    margin-top: ${tk('spacing_sm')};
  }

  @media ${tk('mq_tablet')} {
    > * table {
      width: 100%;
    }

    > * td {
      width: 50%;
    }
  }
`;

export function AccoPriceDetails({ onClose, price, accommodationId }: AccoPriceDetailsProps) {
  const intl = useIntl();
  const { error, isLoading, priceDetails } = useAccoPriceDetails({
    accommodationId,
    departureDate: price.departureDate,
    duration: price.durationInDays,
    mealPlan: price.mealplan.code,
    transportType: price.transportType,
    participants: price.participants,
  });

  return (
    // The `open` is always true because the modal is conditionally rendered
    // by its parent to avoid making an API request until the user clicks
    // on the price details button
    <Modal.Root open onClose={onClose}>
      <Modal.Content $size="md">
        <Modal.Header>
          <Modal.HeaderTitle text={priceDetails?.title ?? ''} />
          <Modal.HeaderCloseButton />
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Modal.Spinner />}
          {error && (
            <StyledErrorMessage direction="y" spacing={tk('spacing_sm')} alignItems="center" justifyContent="center">
              <Icon glyph="IconWarningTriangle" size="md" />
              <Typography as="p" $variant="body-lg-bold">
                {intl.formatMessage({ id: 'price_details_error_msg' })}
              </Typography>
            </StyledErrorMessage>
          )}
          {priceDetails && (
            <StyledPriceDetails
              as="div"
              $variant="body-md"
              dangerouslySetInnerHTML={{ __html: priceDetails.content }}
            />
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
